/**
 * 工匠「對外可用狀態」：結合 DB status / is_on_break 與工作時間表（與儀表板、搜尋共用）
 */

export type CraftsmanAvailability = 'available' | 'busy' | 'resting' | 'off_hours';

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

export function parseWorkingHoursRecord(
  raw: unknown
): Record<string, { enabled?: boolean; startTime?: string; endTime?: string }> | null {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, { enabled?: boolean; startTime?: string; endTime?: string }>;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') {
    return raw as Record<string, { enabled?: boolean; startTime?: string; endTime?: string }>;
  }
  return null;
}

function parseTimeToMinutes(time: string): number | null {
  const [h, m] = time.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/** 是否為工作時間編輯器使用的星期鍵 */
function hasAnyDayKey(wh: Record<string, unknown>): boolean {
  return DAY_KEYS.some((k) => Object.prototype.hasOwnProperty.call(wh, k));
}

/**
 * 依「此刻」判斷是否落在工作表內。
 * - 無任何星期鍵：視為未設定表 → 不套用時段限制
 * - 有鍵但整日皆未 enabled：視為全週關閉 → 非營業
 */
function classifyWorkingMoment(
  wh: Record<string, { enabled?: boolean; startTime?: string; endTime?: string }>,
  now: Date
): 'no_schedule' | 'all_days_off' | 'outside_today_window' | 'inside' {
  if (!hasAnyDayKey(wh)) {
    return 'no_schedule';
  }

  const dayConfigs = DAY_KEYS.map((k) => wh[k]).filter((d) => d != null);
  const anyEnabled = dayConfigs.some((d) => d.enabled === true);
  if (!anyEnabled) {
    return 'all_days_off';
  }

  const dayKey = DAY_KEYS[now.getDay()];
  const day = wh[dayKey];
  if (!day || !day.enabled) {
    return 'outside_today_window';
  }

  const startM = parseTimeToMinutes(day.startTime || '09:00');
  const endM = parseTimeToMinutes(day.endTime || '18:00');
  const cur = now.getHours() * 60 + now.getMinutes();
  if (startM === null || endM === null) {
    return 'inside';
  }
  if (cur >= startM && cur <= endM) {
    return 'inside';
  }
  return 'outside_today_window';
}

export function resolveCraftsmanAvailability(
  craftsman: {
    status?: string | null;
    is_on_break?: boolean | null;
    working_hours?: unknown;
    workingHours?: unknown;
  },
  now: Date = new Date()
): { availability: CraftsmanAvailability; isOnBreak: boolean } {
  const isOnBreak = Boolean(craftsman?.is_on_break);
  const rawStatus = (craftsman?.status || '').toLowerCase();

  if (rawStatus === 'pending') {
    return { availability: 'resting', isOnBreak: false };
  }

  if (rawStatus === 'busy') {
    return { availability: 'busy', isOnBreak };
  }

  const isOpenForBusiness = rawStatus === 'active' || rawStatus === 'available';
  if (!isOpenForBusiness) {
    return { availability: 'resting', isOnBreak: false };
  }

  if (isOnBreak) {
    return { availability: 'busy', isOnBreak: true };
  }

  const whRaw = craftsman.working_hours ?? craftsman.workingHours;
  const wh = parseWorkingHoursRecord(whRaw);
  if (!wh) {
    return { availability: 'available', isOnBreak: false };
  }

  const moment = classifyWorkingMoment(wh, now);
  if (moment === 'all_days_off' || moment === 'outside_today_window') {
    return { availability: 'off_hours', isOnBreak: false };
  }

  return { availability: 'available', isOnBreak: false };
}

/** 搜尋結果排序權重：越小越靠前 */
export function availabilitySortRank(av: CraftsmanAvailability): number {
  if (av === 'available') return 0;
  if (av === 'busy') return 1;
  if (av === 'off_hours') return 2;
  return 3;
}
